import { Link } from "react-router-dom";
import { ArrowRight, Clock, GraduationCap, ShieldCheck, Sparkles, Users, BookOpen, Compass, Heart } from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const MODEL = ["Recognize", "Listen", "Support", "Assess", "Refer"];

export default function PortalLanding() {
  usePageMeta({
    title: "CTA-001 — Grief in the Workplace | CCCHAPS Chaplain Training Academy",
    description: "Free, introductory workplace chaplain training. Register for CTA-001 and learn the CCCHAPS approach to compassionate workplace grief support.",
  });
  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-landing">
      {/* Portal Nav */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/academy/portal" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center"><GraduationCap className="w-5 h-5" strokeWidth={1.75}/></div>
            <div>
              <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Chaplain Training Academy</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/academy/portal/login" data-testid="portal-nav-login" className="text-slate-700 hover:text-[#4A6FA5] font-medium">Sign In</Link>
            <Link to="/academy/portal/register" data-testid="portal-nav-register" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Start Free Course <ArrowRight className="w-3.5 h-3.5"/></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-3">CCCHAPS Academy · CTA-001</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              Grief in the Workplace
            </h1>
            <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-700 leading-tight">
              What Every Chaplain Should Know
            </p>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              Employees do not leave grief at the workplace door. CTA-001 introduces a compassionate, professional approach to workplace grief support and the role of the workplace chaplain.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/academy/portal/register" data-testid="portal-cta-start-free" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] shadow-sm">
                START FREE COURSE <ArrowRight className="w-4 h-4"/>
              </Link>
              <a href="#course-details" data-testid="portal-cta-details" className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
                VIEW COURSE DETAILS
              </a>
            </div>
          </div>
          <aside className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">At a glance</p>
            <dl className="mt-4 space-y-3 text-sm">
              {[
                ["Course", "CTA-001"],
                ["Length", "Approx. 60 minutes"],
                ["Level", "Introductory"],
                ["Format", "Online / Self-Paced"],
                ["Cost", "Free"],
              ].map(([k,v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                  <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 rounded-xl bg-[#F0F4F8] p-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">CCCHAPS Teaching Model</p>
              <p className="mt-1.5 font-heading text-sm text-slate-900 font-medium">
                {MODEL.join("  →  ")}
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Course details */}
      <section id="course-details" className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Course description</p>
            <h2 className="mt-2 font-heading text-3xl text-slate-900 tracking-tight">A compassionate introduction.</h2>
          </div>
          <div className="lg:col-span-2 space-y-5 text-slate-700 leading-relaxed">
            <p>CTA-001 examines how grief and significant loss can affect employees in the workplace and introduces the professional workplace chaplain's role in providing appropriate emotional and spiritual support.</p>
            <p>Participants learn how grief may affect employee behavior, communication, concentration, relationships, attendance, and workplace functioning while developing an understanding of ministry of presence, active listening, professional boundaries, cultural and religious sensitivity, scope of practice, and appropriate referral.</p>
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900">
              This course introduces chaplain support principles. It is not therapy or clinical grief training and does not qualify participants to diagnose or provide clinical treatment.
            </div>
          </div>
        </div>
      </section>

      {/* Who should attend */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Who should attend</p>
        <h2 className="font-heading text-3xl text-slate-900 tracking-tight max-w-2xl">Appropriate for a range of chaplain roles.</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            [Users, "Workplace and organizational chaplains"],
            [Compass, "Community chaplains"],
            [Heart, "Volunteer chaplains"],
            [Sparkles, "Aspiring chaplains"],
            [BookOpen, "Clergy and ministry leaders exploring workplace chaplaincy"],
            [ShieldCheck, "Organizational leaders who want to understand the chaplain's role"],
          ].map(([Icon, label]) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#F0F4F8] text-[#4A6FA5] shrink-0"><Icon className="w-4 h-4" strokeWidth={1.75}/></span>
              <span className="text-sm text-slate-800 leading-relaxed">{label}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-500">No prior CCCHAPS course is required.</p>
      </section>

      {/* Learning objectives */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Learning objectives</p>
            <h2 className="mt-2 font-heading text-3xl text-slate-900 tracking-tight">By completion, participants should be able to:</h2>
          </div>
          <ul className="lg:col-span-2 space-y-2.5 text-slate-700 leading-relaxed text-sm">
            {[
              "Identify common ways grief and loss may present themselves in the workplace.",
              "Explain the workplace chaplain's role when supporting a grieving employee.",
              "Differentiate chaplain support from counseling, therapy, and clinical treatment.",
              "Demonstrate basic principles of ministry of presence and supportive listening.",
              "Identify common responses that may unintentionally harm or minimize grieving employees.",
              "Provide respectful support across different religious, spiritual, cultural, and nonreligious perspectives.",
              "Recognize circumstances in which referral to another professional resource may be appropriate.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0"/>{t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Model + Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">CCCHAPS chaplain teaching model</p>
        <p className="mt-3 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">
          {MODEL.join("  →  ")}
        </p>
        <div className="mt-8">
          <Link to="/academy/portal/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] shadow-sm">
            START FREE COURSE <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <p className="mt-6 text-[11px] text-slate-500 max-w-2xl mx-auto leading-relaxed">
          CTA-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, or authority to provide mental-health diagnosis or clinical treatment.
        </p>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8 text-center text-xs text-slate-500" data-testid="portal-footer">
        <Link to="/academy" className="hover:text-[#4A6FA5]">← Back to CCCHAPS Chaplain Training Academy</Link>
        <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
          <Link to="/academy/portal/privacy" className="hover:text-[#4A6FA5]" data-testid="footer-privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/academy/portal/terms" className="hover:text-[#4A6FA5]" data-testid="footer-terms">Terms</Link>
          <span aria-hidden="true">·</span>
          <Link to="/academy/portal/accessibility" className="hover:text-[#4A6FA5]" data-testid="footer-accessibility">Accessibility</Link>
          <span aria-hidden="true">·</span>
          <Link to="/contact" className="hover:text-[#4A6FA5]" data-testid="footer-contact">Contact &amp; Support</Link>
        </div>
      </footer>
    </div>
  );
}
